import { createAsyncThunk } from '@reduxjs/toolkit';

// Common countries for fallback
const COMMON_COUNTRIES = [
  'Taiwan', 'Thailand', 'Turkey', 'Tanzania', 'Tunisia', 'Tajikistan', 'Togo', 'Timor-Leste', 'Turkmenistan', 'Trinidad and Tobago', 'Tonga', 'Tuvalu',
  'United States', 'United Kingdom', 'United Arab Emirates', 'Uganda', 'Ukraine', 'Uruguay',
  'India', 'Indonesia', 'Iceland', 'Ireland', 'Italy', 'Iran', 'Iraq', 'Israel',
  'China', 'Canada', 'Chile', 'Colombia', 'Cambodia', 'Cameroon', 'Costa Rica', 'Cuba', 'Czechia',
  'Germany', 'Ghana', 'Greece', 'Guatemala', 'Guyana',
  'France', 'Finland', 'Fiji',
  'Japan', 'Jamaica', 'Jordan',
  'Kenya', 'Kuwait', 'Kazakhstan',
  'Mexico', 'Morocco', 'Malaysia', 'Maldives', 'Myanmar', 'Mongolia'
];

// Helper to derive ISO 3166-2 code
const getIso3166_2Code = (loc) => {
  let code = '';
  // 1. Prioritize direct iso3166_2
  if (loc.iso3166_2) {
    code = loc.iso3166_2.replace(/\0/g, '');
  } else if (loc.country_code) {
    // 2. Construct from available sub-country codes
    if (loc.state_code) {
      code = `${loc.country_code.toUpperCase()}-${loc.state_code.toUpperCase()}`.replace(/\0/g, '');
    } else if (loc.county_code) {
      code = `${loc.country_code.toUpperCase()}-${loc.county_code.toUpperCase()}`.replace(/\0/g, '');
    } else if (loc.district_code) {
      code = `${loc.country_code.toUpperCase()}-${loc.district_code.toUpperCase()}`.replace(/\0/g, '');
    }
  }

  // 3. Specific overrides for known discrepancies
  // Example: Manchester, UK (Geoapify might return GB-ENG or GB-SLF for county)
  if (loc.name === 'Manchester' && loc.country_code === 'gb' && (loc.result_type === 'county' || loc.result_type === 'city')) {
    code = 'GB-MAN';
  }
  // Add more overrides here as needed

  return code;
};

// Map Geoapify result_type to our application types
const mapLocationType = (resultType, scale) => {
  if (resultType === 'city') return 'city';
  if (resultType === 'town') return 'town';
  if (resultType === 'village') return 'village';
  if (resultType === 'state') return 'state';
  if (resultType === 'country') return 'country';
  if (resultType === 'district') return 'district';
  if (resultType === 'county') return 'county';
  if (resultType === 'locality') return 'locality';
  return scale;
};

// Format location response
const formatLocation = (location, scale) => {
  const locationType = mapLocationType(location.result_type, scale);
  const regionCode = getIso3166_2Code(location);

  // Extract city name properly - prefer location.city, then location.name
  // If both are empty, extract just the city part from formatted address
  let cityName = location.city || location.name;
  if (!cityName && location.formatted) {
    // Extract first part before comma (usually the city name)
    cityName = location.formatted.split(',')[0].trim();
  }
  cityName = cityName || 'Unknown';

  return {
    id: location.place_id,
    name: cityName,
    region: regionCode,
    country: location.country,
    countryCode: location.country_code,
    lat: location.lat,
    lon: location.lon,
    type: locationType,
    description: location.formatted,
  };
};

// Build Geoapify URL
const buildGeoapifyUrl = (searchTerm, placeType, includeType) => {
  const u = new URL('https://api.geoapify.com/v1/geocode/autocomplete');
  u.searchParams.append('text', searchTerm);
  if (includeType && placeType) {
    u.searchParams.append('type', placeType);
  }
  u.searchParams.append('format', 'json');
  u.searchParams.append('lang', 'en');
  u.searchParams.append('limit', '20');
  const apiKey = process.env.REACT_APP_GEOAPIFY_API_KEY || 'da024b47d29844ffa3009ee5b5df7d81';
  u.searchParams.append('apiKey', apiKey);
  return u;
};

// Get fallback countries for empty results
const getFallbackCountries = (searchTerm) => {
  const term = searchTerm.trim().toLowerCase();
  if (term.length < 1) return [];

  return COMMON_COUNTRIES
    .filter(name => name.toLowerCase().startsWith(term))
    .slice(0, 10)
    .map(name => ({
      id: `fallback-${name}`,
      name,
      region: '',
      country: name,
      countryCode: '',
      lat: null,
      lon: null,
      type: 'country',
      description: name,
    }));
};

export const searchLocations = createAsyncThunk(
  'issues/searchLocations',
  async ({ scale, searchTerm }, { rejectWithValue }) => {
    try {
      // Safely check if searchTerm exists and is not empty
      if (!searchTerm || !searchTerm.trim()) {
        return [];
      }

      // Map scale to Geoapify place type with fallbacks
      const placeType = {
        'country': 'country',
        'national': 'country',
        'state': 'state',
        'regional': 'state',
        'city': 'city',
        'local': 'city',
        'global': null // global doesn't need a specific type
      }[scale] || null;

      try {
        // 1) First try with type filter (respects selected scale)
        let response = await fetch(buildGeoapifyUrl(searchTerm, placeType, true).toString());
        if (!response.ok) {
          throw new Error(`Failed to fetch locations: ${response.status}`);
        }

        let data = await response.json();
        let results = Array.isArray(data.results) ? data.results : [];

        // 2) Fallback: if no results and we filtered by type, retry WITHOUT type filter to broaden matches
        if ((!results || results.length === 0) && placeType && searchTerm.trim().length >= 1) {
          const resp2 = await fetch(buildGeoapifyUrl(searchTerm, placeType, false).toString());
          if (resp2.ok) {
            const data2 = await resp2.json();
            results = Array.isArray(data2.results) ? data2.results : [];
          }
        }

        // Still empty -> provide lightweight local fallback for 1+ char queries
        if (!results || results.length === 0) {
          const fallback = getFallbackCountries(searchTerm);
          return fallback.length > 0 ? fallback : [];
        }

        // Format the response to match our needs
        return results.map(location => formatLocation(location, scale));
      } catch (fetchError) {
        throw fetchError;
      }

    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
